import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { Truck, MapPin, Calendar, Package, Phone, ArrowLeft, CheckCircle, Clock, XCircle, Star, ChevronDown, ChevronUp } from 'lucide-react';
import logger from '../utils/logger';
import axios from 'axios';
import { API_ENDPOINTS } from '../config/api';
import DriverRatingModal from '../components/DriverRatingModal';
import AddressAutocomplete from '../components/AddressAutocomplete';
import ItemSelector from '../components/ItemSelector';
import { HEAVY_FURNITURE_ITEMS, ELECTRICAL_ITEMS } from '../data/itemsCatalog';
import { MOVING_CATEGORY_OPTIONS, MOVING_CATEGORIES } from '../data/movingCategories';
import { BUILDING_TYPE_OPTIONS, ACCESS_PATH_OPTIONS_ARRAY } from '../data/addressConstants';

// Status message mapping for offers
const getOfferStatusMessage = (status) => {
  const statusMessages = {
    'date_unavailable': 'תאריך זה נלקח',
    'pending': 'ממתין לתגובה',
    'accepted': 'התקבל',
    'rejected': 'נדחה',
    'withdrawn': 'בוטל',
    'pending_reapproval': 'עדכון מחיר - ממתין לאישור'
  };
  return statusMessages[status] || 'לא ידוע';
};

const RequestTrackingPage = () => {
  const { requestId } = useParams();
  const navigate = useNavigate();
  const [request, setRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Verification states
  const [showVerification, setShowVerification] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [sessionToken, setSessionToken] = useState(null);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [showCancelOfferModal, setShowCancelOfferModal] = useState(false);
  const [cancelOfferReason, setCancelOfferReason] = useState('');
  
  // Edit confirmation state
  const [showEditConfirmationModal, setShowEditConfirmationModal] = useState(false);
  
  // Edit mode states
  const [isEditMode, setIsEditMode] = useState(false);
  const [editData, setEditData] = useState({});
  
  // Address validation data for edit mode
  const [originAddressData, setOriginAddressData] = useState(null);
  const [destinationAddressData, setDestinationAddressData] = useState(null);
  
  // Rating states
  const [showRatingModal, setShowRatingModal] = useState(false);
  const [canRate, setCanRate] = useState(false);
  const [ratingChecked, setRatingChecked] = useState(false);
  
  // Job completion states
  const [showCloseModal, setShowCloseModal] = useState(false);
  const [finalPrice, setFinalPrice] = useState('');
  const [isClosing, setIsClosing] = useState(false);
  
  // Items section collapse states (default: closed)
  const [isHeavyFurnitureExpanded, setIsHeavyFurnitureExpanded] = useState(false);
  const [isElectricalItemsExpanded, setIsElectricalItemsExpanded] = useState(false);

  // Check if request can be rated
  const checkRatingEligibility = useCallback(async (requestData) => {
    if (requestData.status === 'closed' && requestData.selectedDriver && !ratingChecked) {
      try {
        const response = await axios.get(`${API_ENDPOINTS.RATINGS}/can-rate/${requestData.id}/${requestData.customerPhone}`);
        setCanRate(response.data.canRate);
        setRatingChecked(true);
      } catch (error) {
        logger.error('Error checking rating eligibility:', error);
        setCanRate(false);
      }
    }
  }, [ratingChecked]);

  // Fetch request data
  const fetchRequest = useCallback(async () => {
    try {
      const response = await axios.get(`${API_ENDPOINTS.REQUESTS}/track/${requestId}`);
      setRequest(response.data.data);
    } catch (error) {
      logger.error('Error fetching request:', error);
      toast.error('שגיאה בטעינת נתוני הבקשה');
    }
  }, [requestId]);

  // Handle job completion
  const handleJobCompletion = async () => {
    if (!sessionToken) {
      toast.error('יש לאמת את הזהות תחילה');
      setShowVerification(true);
      return;
    }

    setIsClosing(true);
    try {
      await axios.post(`${API_ENDPOINTS.REQUESTS}/${requestId}/customer-close`, {
        sessionToken,
        finalPrice: finalPrice ? parseFloat(finalPrice) : undefined
      });

      toast.success('ההובלה הושלמה בהצלחה!');
      setShowCloseModal(false);
      setFinalPrice('');
      
      // Refresh request data
      await fetchRequest();
      
    } catch (error) {
      logger.error('Job completion error:', error.response?.data);
      if (error.response?.status === 401) {
        toast.error('פג תוקף האימות. יש לאמת את הזהות מחדש');
        setSessionToken(null);
        setShowVerification(true);
      } else {
        toast.error(error.response?.data?.message || 'שגיאה בסגירת הבקשה');
      }
    } finally {
      setIsClosing(false);
    }
  };

  useEffect(() => {
    const fetchInitial = async () => {
      try {
        const response = await axios.get(`${API_ENDPOINTS.REQUESTS}/track/${requestId}`);
        const requestData = response.data.data;
        setRequest(requestData);
        
        // Check rating eligibility for closed requests
        await checkRatingEligibility(requestData);
        
        // Require a valid session token for viewing the page
        const storedToken = sessionStorage.getItem(`request_session_${requestId}`);
        if (storedToken) {
          setSessionToken(storedToken);
        } else {
          setShowVerification(true);
        }
        
        setLoading(false);
      } catch (error) {
        setError(error.response?.data?.message || 'שגיאה בטעינת הבקשה');
        setLoading(false);
      }
    };
    fetchInitial();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestId]);

  // Poll for changes every 5 minutes while page is open
  useEffect(() => {
    const intervalId = setInterval(async () => {
      try {
        const response = await axios.get(`${API_ENDPOINTS.REQUESTS}/track/${requestId}`);
        const next = response.data.data;
        // Detect notable changes to notify user
        if (request) {
          const prevOffers = request.offers?.length || 0;
          const nextOffers = next.offers?.length || 0;
          if (nextOffers > prevOffers) {
            toast.success('התקבלה הצעת מחיר חדשה!');
          }
          if (request.status !== 'closed' && next.status === 'closed') {
            toast.success('הבקשה נסגרה בהצלחה!');
            // Check rating eligibility for newly closed requests
            checkRatingEligibility(next);
          }
        }
        setRequest(next);
      } catch {
        // ignore polling errors to avoid noise
      }
    }, 300000);

    return () => clearInterval(intervalId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestId, request]);

  // Verify SMS code and get session token for modification
  const verifyCode = async () => {
    if (!verificationCode.trim()) {
      toast.error('יש להזין קוד אימות');
      return;
    }

    setIsVerifying(true);
    try {
      // Use POST endpoint for modification verification
      const response = await axios.post(`${API_ENDPOINTS.REQUESTS}/${requestId}/verify-customer`, {
        phone: request.customerPhone,
        code: verificationCode
      });
      
      const token = response.data.data.sessionToken;
      setSessionToken(token);
      sessionStorage.setItem(`request_session_${requestId}`, token);
      setShowVerification(false);
      
      toast.success('מספר הטלפון אומת בהצלחה');
    } catch (error) {
      toast.error(error.response?.data?.message || 'קוד האימות שגוי');
    } finally {
      setIsVerifying(false);
    }
  };

  // Update request
  const updateRequest = async (confirmed = false) => {
    if (!sessionToken) {
      toast.error('יש לאמת את הזהות תחילה');
      setShowVerification(true);
      return;
    }

    // Check if request is accepted and needs confirmation
    // Note: confirmed is passed as true when called from the modal
    if (request.status === 'offer_accepted' && confirmed !== true) {
       setShowEditConfirmationModal(true);
       return;
    }

    setIsUpdating(true);
    try {
      // Prepare update data with address validation data if available
      // Clean up empty strings for optional fields
      const cleanedEditData = { ...editData };
      
      // If category is somehow empty, default it to Apartment or omit it
      if (cleanedEditData.category === '') {
        cleanedEditData.category = undefined; // Omit so it doesn't change
      }

      // Convert empty strings to null for optional fields to allow clearing in DB
      const fieldsToNull = [
        'originNumberOfRooms', 
        'destinationNumberOfRooms', 
        'originBuildingType', 
        'destinationBuildingType'
      ];
      
      fieldsToNull.forEach(field => {
        if (cleanedEditData[field] === '') {
          cleanedEditData[field] = null;
        }
      });

      // Ensure required number fields are at least 0
      const numberFields = ['numberOfBoxes', 'numberOfBags', 'numberOfClothingBags'];
      numberFields.forEach(field => {
        if (cleanedEditData[field] === '' || cleanedEditData[field] === null || cleanedEditData[field] === undefined) {
          cleanedEditData[field] = 0;
        }
      });
      
      const updatePayload = {
        ...cleanedEditData,
        sessionToken,
        confirmEditAfterAcceptance: confirmed === true
      };

      // Add origin address validation data if available
      if (originAddressData && originAddressData.isValid) {
        updatePayload.originLatitude = originAddressData.latitude;
        updatePayload.originLongitude = originAddressData.longitude;
        updatePayload.originPlaceId = originAddressData.placeId;
        updatePayload.originAddressFormatted = originAddressData.address;
      }

      // Add destination address validation data if available
      if (destinationAddressData && destinationAddressData.isValid) {
        updatePayload.destinationLatitude = destinationAddressData.latitude;
        updatePayload.destinationLongitude = destinationAddressData.longitude;
        updatePayload.destinationPlaceId = destinationAddressData.placeId;
        updatePayload.destinationAddressFormatted = destinationAddressData.address;
      }

      const response = await axios.put(`${API_ENDPOINTS.REQUESTS}/${requestId}`, updatePayload);
      
      setRequest(response.data.data.request);
      setIsEditMode(false);
      setSessionToken(null);
      setEditData({});
      setOriginAddressData(null);
      setDestinationAddressData(null);
      toast.success('הבקשה עודכנה בהצלחה');
    } catch (error) {
      logger.error('Update request error:', error.response?.data);
      if (error.response?.status === 401) {
        toast.error('פג תוקף האימות. יש לאמת את הזהות מחדש');
        setSessionToken(null);
        setShowVerification(true);
      } else {
        toast.error(error.response?.data?.message || 'שגיאה בעדכון הבקשה');
      }
    } finally {
      setIsUpdating(false);
    }
  };

  // Accept offer
  const acceptOffer = async (offerId) => {
    try {
      const response = await axios.post(`${API_ENDPOINTS.REQUESTS}/${requestId}/accept-offer`, {
        offerId
      });
      
      setRequest(response.data.data.request);
      toast.success('ההצעה התקבלה בהצלחה!');
    } catch (error) {
      toast.error(error.response?.data?.message || 'שגיאה בקבלת ההצעה');
    }
  };

  // Cancel accepted offer
  const cancelOffer = async () => {
    if (!sessionToken) {
      toast.error('יש לאמת את הזהות תחילה');
      return;
    }

    try {
      const response = await axios.post(`${API_ENDPOINTS.REQUESTS}/${requestId}/cancel-offer`, {
        sessionToken,
        reason: cancelOfferReason
      });
      
      setRequest(response.data.data.request);
      setShowCancelOfferModal(false);
      setCancelOfferReason('');
      setSessionToken(null);
      
      const { cancellationFee, cancellationFeePercentage } = response.data.data;
      if (cancellationFee > 0) {
        toast.success(`ההצעה בוטלה בהצלחה. עלות ביטול: ${cancellationFeePercentage}% (₪${cancellationFee})`);
      } else {
        toast.success('ההצעה בוטלה בהצלחה ללא עלות');
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'שגיאה בביטול ההצעה');
    }
  };

  // Cancel request
  const cancelRequest = async () => {
    if (!sessionToken) {
      toast.error('יש לאמת את הזהות תחילה');
      return;
    }

    try {
      const response = await axios.post(`${API_ENDPOINTS.REQUESTS}/${requestId}/cancel`, {
        sessionToken,
        reason: cancelReason
      });
      
      setRequest(response.data.data.request);
      setShowCancelModal(false);
      setCancelReason('');
      setSessionToken(null);
      toast.success('הבקשה בוטלה בהצלחה');
    } catch (error) {
      toast.error(error.response?.data?.message || 'שגיאה בביטול הבקשה');
    }
  };

  // Handle edit mode toggle
  const handleEditToggle = () => {
    if (isEditMode) {
      setIsEditMode(false);
      setSessionToken(null);
      setEditData({});
      setOriginAddressData(null);
      setDestinationAddressData(null);
    } else {
      setIsEditMode(true);
      // Initialize edit data with current request data
      const originStreet = request.originAddress?.street || request.originAddress || '';
      const destinationStreet = request.destinationAddress?.street || request.destinationAddress || '';
      
      const normalizeItems = (items, prefix) => {
          return (items || []).map(item => {
              if (typeof item === 'string') {
                  return {
                      name: item,
                      isCustom: true,
                      quantity: 1,
                      id: `${prefix}-${Date.now()}-${Math.random()}`
                  };
              }
              return {
                  ...item,
                  id: item.id || `${prefix}-${Date.now()}-${Math.random()}`
              };
          });
      };

      setEditData({
        customerName: request.customerName,
        category: request.category || MOVING_CATEGORIES.APARTMENT,
        originAddress: originStreet,
        originFloor: request.originAddress?.floor || request.originFloor || 0,
        originHasElevator: request.originAddress?.hasElevator ?? request.originHasElevator ?? false,
        originBuildingType: request.originAddress?.buildingType || '',
        originNumberOfRooms: request.originAddress?.numberOfRooms || '',
        originAccessPath: request.originAddress?.accessPath || [],
        destinationAddress: destinationStreet,
        destinationFloor: request.destinationAddress?.floor || request.destinationFloor || 0,
        destinationHasElevator: request.destinationAddress?.hasElevator ?? request.destinationHasElevator ?? false,
        destinationBuildingType: request.destinationAddress?.buildingType || '',
        destinationNumberOfRooms: request.destinationAddress?.numberOfRooms || '',
        destinationAccessPath: request.destinationAddress?.accessPath || [],
        preferredDate: request.preferredDate ? new Date(request.preferredDate).toISOString().split('T')[0] : '',
        numberOfBoxes: request.numberOfBoxes || 0,
        numberOfBags: request.numberOfBags || 0,
        numberOfClothingBags: request.numberOfClothingBags || 0,
        requiresCrane: request.requiresCrane,
        heavyFurniture: normalizeItems(request.heavyFurniture, 'hf'),
        electricalItems: normalizeItems(request.electricalItems, 'ei'),
        additionalNotes: request.additionalNotes || ''
      });

      // Initialize address validation data if addresses are already validated
      if (request.originAddress?.latitude && request.originAddress?.longitude) {
        setOriginAddressData({
          address: request.originAddress?.formatted || originStreet,
          latitude: request.originAddress.latitude,
          longitude: request.originAddress.longitude,
          placeId: request.originAddress.placeId,
          isValid: true
        });
      }

      if (request.destinationAddress?.latitude && request.destinationAddress?.longitude) {
        setDestinationAddressData({
          address: request.destinationAddress?.formatted || destinationStreet,
          latitude: request.destinationAddress.latitude,
          longitude: request.destinationAddress.longitude,
          placeId: request.destinationAddress.placeId,
          isValid: true
        });
      }
    }
  };

  // Handle cancel request
  const handleCancelRequest = () => {
    if (!sessionToken) {
      setShowVerification(true);
    } else {
      setShowCancelModal(true);
    }
  };

  // Handle cancel offer
  const handleCancelOffer = () => {
    if (!sessionToken) {
      setShowVerification(true);
    } else {
      setShowCancelOfferModal(true);
    }
  };

  // Calculate cancellation info
  const getCancellationInfo = () => {
    if (!request?.offerAcceptedAt) return null;
    
    const now = new Date();
    const offerAcceptedAt = new Date(request.offerAcceptedAt);
    const movingDate = new Date(request.preferredDate);
    
    const hoursSinceAcceptance = (now - offerAcceptedAt) / (1000 * 60 * 60);
    const hoursUntilMoving = (movingDate - now) / (1000 * 60 * 60);
    
    let cancellationFee = 0;
    let cancellationFeePercentage = 0;
    let canCancelFree = false;
    
    if (hoursSinceAcceptance <= 48) {
      canCancelFree = true;
      cancellationFee = 0;
      cancellationFeePercentage = 0;
    } else if (hoursUntilMoving >= 48) {
      cancellationFee = Math.round(request.selectedPrice * 0.1);
      cancellationFeePercentage = 10;
    } else {
      cancellationFee = Math.round(request.selectedPrice * 0.5);
      cancellationFeePercentage = 50;
    }
    
    return {
      hoursSinceAcceptance: Math.round(hoursSinceAcceptance),
      hoursUntilMoving: Math.round(hoursUntilMoving),
      cancellationFee,
      cancellationFeePercentage,
      canCancelFree
    };
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 to-brown-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-orange-600 mx-auto mb-4"></div>
          <p className="text-brown-700 text-lg">טוען...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 to-brown-50 flex items-center justify-center">
        <div className="card max-w-md w-full text-center">
          <XCircle className="h-16 w-16 text-error-600 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-brown-900 mb-4">שגיאה</h2>
          <p className="text-brown-700 mb-6">{error}</p>
          <button
            onClick={() => navigate('/')}
            className="btn-primary w-full"
          >
            חזור לדף הבית
          </button>
        </div>
      </div>
    );
  }

  if (!request) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 to-brown-50 flex items-center justify-center">
        <div className="card max-w-md w-full text-center">
          <XCircle className="h-16 w-16 text-error-600 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-brown-900 mb-4">בקשה לא נמצאה</h2>
          <p className="text-brown-700 mb-6">הבקשה המבוקשת לא נמצאה במערכת</p>
          <button
            onClick={() => navigate('/')}
            className="btn-primary w-full"
          >
            חזור לדף הבית
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-brown-50 py-12">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Skip link target */}
        <div id="main-content" className="sr-only" tabIndex="-1">תוכן ראשי</div>
        {/* Header */}
        <div id="navigation" className="mb-10" tabIndex="-1">
          <button
            onClick={() => navigate('/')}
            className="flex items-center text-orange-600 hover:text-orange-700 mb-6 text-lg font-semibold transition-colors duration-300"
          >
            <ArrowLeft className="h-5 w-5 ml-3" />
            חזור לדף הבית
          </button>
          <h1 className="text-4xl font-bold text-brown-900 mb-4">מעקב אחרי הזמנה</h1>
          <p className="text-brown-700 text-xl">מספר הזמנה: <span className="font-bold text-orange-600">{requestId}</span></p>
        </div>


        {/* Status Overview */}
        <div className="card mb-8 hover-lift">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-brown-900">סטטוס הבקשה</h2>
            <div className="flex items-center space-x-2 space-x-reverse">
              {request.status === 'open' && (
                <div className="flex items-center text-orange-600">
                  <Clock className="h-5 w-5 ml-2" />
                  <span className="font-semibold">פתוחה</span>
                </div>
              )}
              {request.status === 'in_progress' && (
                <div className="flex items-center text-blue-600">
                  <Clock className="h-5 w-5 ml-2" />
                  <span className="font-semibold">בתהליך</span>
                </div>
              )}
              {request.status === 'offer_accepted' && !request.offers?.some(offer => offer.status === 'withdrawn' && request.offerAcceptedOfferId?.toString() === offer._id.toString()) && (
                <div className="flex items-center text-green-600">
                  <CheckCircle className="h-5 w-5 ml-2" />
                  <span className="font-semibold">הצעה התקבלה</span>
                </div>
              )}
              {request.status === 'offer_accepted' && request.offers?.some(offer => offer.status === 'withdrawn' && request.offerAcceptedOfferId?.toString() === offer._id.toString()) && (
                <div className="flex items-center text-orange-600">
                  <Clock className="h-5 w-5 ml-2" />
                  <span className="font-semibold">בתהליך (הצעה בוטלה)</span>
                </div>
              )}
              {request.status === 'offer_pending_reapproval' && (
                <div className="flex items-center text-orange-600">
                  <Clock className="h-5 w-5 ml-2" />
                  <span className="font-semibold">ממתין לאישור מחיר</span>
                </div>
              )}
              {request.status === 'closed' && (
                <div className="flex items-center text-success-600">
                  <CheckCircle className="h-5 w-5 ml-2" />
                  <span className="font-semibold">נסגרה</span>
                </div>
              )}
              {request.status === 'cancelled' && (
                <div className="flex items-center text-error-600">
                  <XCircle className="h-5 w-5 ml-2" />
                  <span className="font-semibold">בוטלה</span>
                </div>
              )}
            </div>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6">
            <div className="text-center p-4 bg-orange-50 rounded-xl">
              <p className="text-sm text-brown-600 mb-1">הצעות שהתקבלו</p>
              <p className="text-2xl font-bold text-orange-600">{request.offers?.length || 0}</p>
            </div>
            <div className="text-center p-4 bg-brown-50 rounded-xl">
              <p className="text-sm text-brown-600 mb-1">תאריך הובלה</p>
              <p className="text-lg font-semibold text-brown-800">{request.preferredDate ? (() => {
                try {
                  const date = new Date(request.preferredDate);
                  return isNaN(date.getTime()) ? 'לא ידוע' : date.toLocaleDateString('he-IL');
                } catch (error) {
                  return 'לא ידוע';
                }
              })() : 'לא ידוע'}</p>
            </div>
            <div className="text-center p-4 bg-orange-50 rounded-xl">
              <p className="text-sm text-brown-600 mb-1">תאריך יצירה</p>
              <p className="text-lg font-semibold text-brown-800">{new Date(request.createdAt).toLocaleDateString('he-IL')}</p>
            </div>
          </div>
        </div>

        {/* Cancelled Accepted Offer Warning */}
        {(() => {
          // Check if there was a previously accepted offer that is now withdrawn
          const hasCancelledAcceptedOffer = request.offers?.some(offer => 
            offer.status === 'withdrawn' && 
            request.offerAcceptedOfferId?.toString() === offer._id.toString()
          );
          
          // Show warning if request is in progress and has a cancelled accepted offer
          // OR if request is still showing as accepted but has withdrawn offers
          const shouldShowWarning = (request.status === 'in_progress' && hasCancelledAcceptedOffer) ||
                                   (request.status === 'offer_accepted' && hasCancelledAcceptedOffer);
          
          return shouldShowWarning;
        })() && (
          <div className="card mb-8 hover-lift border-2 border-red-200 bg-red-50">
            <h3 className="text-xl font-bold text-red-900 mb-6 flex items-center">
              <div className="bg-red-100 p-3 rounded-full ml-3">
                <XCircle className="h-5 w-5 text-red-600" />
              </div>
              הצעה שנבחרה בוטלה על ידי המוביל
            </h3>
            <div className="bg-red-100 p-4 rounded-xl">
              <p className="text-red-800 font-semibold mb-2">
                המוביל שבו בחרת ביטל את ההצעה שלו
              </p>
              <p className="text-red-700 text-sm">
                הבקשה חזרה לסטטוס "בתהליך" ואתה יכול לבחור הצעה אחרת או לחכות להצעות נוספות
              </p>
            </div>
          </div>
        )}

        {/* Accepted Offer Info - Only show if not cancelled */}
        {request.status === 'offer_accepted' && request.selectedDriverName && 
         !request.offers?.some(offer => offer.status === 'withdrawn' && request.offerAcceptedOfferId?.toString() === offer._id.toString()) && (
          <div className="card mb-8 hover-lift border-2 border-green-200 bg-green-50">
            <h3 className="text-xl font-bold text-green-900 mb-6 flex items-center">
              <div className="bg-green-100 p-3 rounded-full ml-3">
                <CheckCircle className="h-5 w-5 text-green-600" />
              </div>
              הצעה שנבחרה
            </h3>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h4 className="text-lg font-semibold text-green-800 mb-2">{request.selectedDriverName}</h4>
                <p className="text-green-700">מחיר: <span className="font-bold text-2xl">₪{request.selectedPrice}</span></p>
                <p className="text-sm text-green-600 mt-2">
                  נבחר ב: {new Date(request.offerAcceptedAt).toLocaleDateString('he-IL')}
                </p>
                {/* Driver phone reveal after 48h window */}
                {(() => {
                  const acceptedOffer = request.offers?.find(o => request.offerAcceptedOfferId?.toString() === o._id?.toString());
                  if (!acceptedOffer) return null;
                  if (request.phoneRevealAllowed && acceptedOffer.driverPhone) {
                    return (
                      <p className="text-green-700 mt-2">
                        טלפון המוביל: <a href={`tel:${acceptedOffer.driverPhone}`} className="font-bold underline">{acceptedOffer.driverPhone}</a>
                      </p>
                    );
                  }
                  return (
                    <p className="text-sm text-brown-600 mt-2">
                      מספרי הטלפון ייחשפו לאחר מעבר חלון הביטול (48 שעות מקבלת ההצעה)
                    </p>
                  );
                })()}
              </div>
              <div className="flex flex-col justify-center">
                {(() => {
                  const cancellationInfo = getCancellationInfo();
                  if (!cancellationInfo) return null;
                  
                  return (
                    <div className="text-sm">
                      <p className="text-green-700 mb-2">
                        {cancellationInfo.canCancelFree ? (
                          <span className="text-green-600 font-semibold">ניתן לבטל ללא עלות</span>
                        ) : (
                          <span className="text-orange-600 font-semibold">
                            עלות ביטול: {cancellationInfo.cancellationFeePercentage}% (₪{cancellationInfo.cancellationFee})
                          </span>
                        )}
                      </p>
                      <p className="text-green-600 text-xs">
                        {cancellationInfo.hoursSinceAcceptance} שעות מאז הקבלה
                      </p>
                    </div>
                  );
                })()}
              </div>
            </div>
            
            {/* Job Completion Button for offer_accepted status */}
            {request.status === 'offer_accepted' && !request.offers?.some(offer => offer.status === 'withdrawn' && request.offerAcceptedOfferId?.toString() === offer._id.toString()) && (
              <div className="mt-6 pt-6 border-t border-green-200">
                <div className="text-center">
                  <p className="text-green-700 mb-4">האם ההובלה הושלמה?</p>
                  <button
                    onClick={() => setShowCloseModal(true)}
                    className="btn-primary flex items-center justify-center space-x-2 space-x-reverse mx-auto"
                  >
                    <CheckCircle className="h-5 w-5" />
                    <span>אשר השלמת הובלה</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Closed Request Info - Show selected driver and rating prompt */}
        {request.status === 'closed' && request.selectedDriverName && (
          <div className="card mb-8 hover-lift border-2 border-green-200 bg-green-50">
            <h3 className="text-xl font-bold text-green-900 mb-6 flex items-center">
              <div className="bg-green-100 p-3 rounded-full ml-3">
                <CheckCircle className="h-5 w-5 text-green-600" />
              </div>
              הובלה הושלמה
            </h3>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h4 className="text-lg font-semibold text-green-800 mb-2">{request.selectedDriverName}</h4>
                <p className="text-green-700">מחיר: <span className="font-bold text-2xl">₪{request.selectedPrice}</span></p>
                <p className="text-sm text-green-600 mt-2">
                  נסגר ב: {new Date(request.closedAt).toLocaleDateString('he-IL')}
                </p>
              </div>
              <div className="flex flex-col justify-center">
                {canRate ? (
                  <div className="text-center">
                    <p className="text-green-700 mb-4">איך הייתה החוויה שלך?</p>
                    <button
                      onClick={() => setShowRatingModal(true)}
                      className="btn-primary flex items-center justify-center space-x-2 space-x-reverse"
                    >
                      <Star className="h-5 w-5" />
                      <span>דרג את המוביל</span>
                    </button>
                  </div>
                ) : (
                  <div className="text-center">
                    <p className="text-green-700">תודה על השימוש בשירות שלנו!</p>
                    <p className="text-sm text-green-600 mt-2">
                      {ratingChecked ? 'כבר דירגת את המוביל' : 'הדירוג לא זמין כרגע'}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Request Details */}
        <div className="grid lg:grid-cols-2 gap-8 mb-8">
          {/* Customer Information */}
          <div className="card hover-lift">
            <h3 className="text-xl font-bold text-brown-900 mb-6 flex items-center">
              <div className="bg-orange-100 p-3 rounded-full ml-3">
                <Phone className="h-5 w-5 text-orange-600" />
              </div>
              פרטי הלקוח
            </h3>
            <div className="space-y-4">
              <div>
                <p className="text-sm text-brown-600 mb-1">שם הלקוח</p>
                <p className="text-lg font-semibold text-brown-900">{request.customerName}</p>
              </div>
              <div>
                <p className="text-sm text-brown-600 mb-1">מספר טלפון</p>
                <p className="text-lg font-semibold text-brown-900">{request.customerPhone}</p>
              </div>
            </div>
          </div>

          {/* Moving Details */}
          <div className="card hover-lift">
            <h3 className="text-xl font-bold text-brown-900 mb-6 flex items-center">
              <div className="bg-orange-100 p-3 rounded-full ml-3">
                <Calendar className="h-5 w-5 text-orange-600" />
              </div>
              פרטי ההובלה
            </h3>
            <div className="space-y-4">
              <div>
                <p className="text-sm text-brown-600 mb-1">תאריך הובלה</p>
                <p className="text-lg font-semibold text-brown-900">{request.preferredDate ? (() => {
                  try {
                    const date = new Date(request.preferredDate);
                    return isNaN(date.getTime()) ? 'לא ידוע' : date.toLocaleDateString('he-IL');
                } catch (error) {
                    return 'לא ידוע';
                  }
                })() : 'לא ידוע'}</p>
              </div>
              <div>
                <p className="text-sm text-brown-600 mb-1">שעת הובלה</p>
                <p className="text-lg font-semibold text-brown-900">לא צוין</p>
              </div>
              {request.category && (
                <div>
                  <p className="text-sm text-brown-600 mb-1">סוג הובלה</p>
                  <span className="inline-block bg-blue-100 text-blue-800 text-sm font-semibold px-3 py-1 rounded-full">
                    {request.category}
                  </span>
                </div>
              )}
              {request.requiresCrane && (
                <div className="flex items-center text-orange-600">
                  <CheckCircle className="h-4 w-4 ml-2" />
                  <span className="font-semibold">נדרש מנוף</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Addresses */}
        <div className="grid lg:grid-cols-2 gap-8 mb-8">
          {/* Origin Address */}
          <div className="card hover-lift">
            <h3 className="text-xl font-bold text-brown-900 mb-6 flex items-center">
              <div className="bg-orange-100 p-3 rounded-full ml-3">
                <MapPin className="h-5 w-5 text-orange-600" />
              </div>
              כתובת המוצא
            </h3>
            <div className="space-y-4">
              <div>
                <p className="text-sm text-brown-600 mb-1">כתובת</p>
                <p className="text-lg font-semibold text-brown-900">{request.originAddress?.street || request.originAddress || 'לא ידוע'}</p>
              </div>
              {request.originAddress?.city && (
                <div>
                  <p className="text-sm text-brown-600 mb-1">עיר</p>
                  <p className="text-lg font-semibold text-brown-900">{request.originAddress.city}</p>
                </div>
              )}
              <div>
                <p className="text-sm text-brown-600 mb-1">קומה</p>
                <p className="text-lg font-semibold text-brown-900">{request.originAddress?.floor ?? request.originFloor ?? 'לא ידוע'}</p>
              </div>
              {(request.originAddress?.hasElevator ?? request.originHasElevator) !== undefined && (
                <div className="flex items-center">
                  {(request.originAddress?.hasElevator ?? request.originHasElevator) ? (
                    <div className="flex items-center text-green-600">
                      <CheckCircle className="h-4 w-4 ml-2" />
                      <span className="font-semibold">יש מעלית</span>
                    </div>
                  ) : (
                    <div className="flex items-center text-brown-500">
                      <XCircle className="h-4 w-4 ml-2" />
                      <span className="font-semibold">אין מעלית</span>
                    </div>
                  )}
                </div>
              )}
              {request.originAddress?.buildingType && (
                <div>
                  <p className="text-sm text-brown-600 mb-1">סוג הבית</p>
                  <p className="text-lg font-semibold text-brown-900">{request.originAddress.buildingType}</p>
                </div>
              )}
              {request.originAddress?.numberOfRooms !== undefined && (
                <div>
                  <p className="text-sm text-brown-600 mb-1">מספר חדרים</p>
                  <p className="text-lg font-semibold text-brown-900">{request.originAddress.numberOfRooms}</p>
                </div>
              )}
              {request.originAddress?.accessPath && request.originAddress.accessPath.length > 0 && (
                <div>
                  <p className="text-sm text-brown-600 mb-1">שביל גישה</p>
                  <div className="flex flex-wrap gap-2">
                    {request.originAddress.accessPath.map((path, idx) => (
                      <span key={idx} className="inline-block bg-orange-100 text-orange-800 text-sm font-semibold px-3 py-1 rounded-full">
                        {path}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Destination Address */}
          <div className="card hover-lift">
            <h3 className="text-xl font-bold text-brown-900 mb-6 flex items-center">
              <div className="bg-orange-100 p-3 rounded-full ml-3">
                <MapPin className="h-5 w-5 text-orange-600" />
              </div>
              כתובת היעד
            </h3>
            <div className="space-y-4">
              <div>
                <p className="text-sm text-brown-600 mb-1">כתובת</p>
                <p className="text-lg font-semibold text-brown-900">{request.destinationAddress?.street || request.destinationAddress || 'לא ידוע'}</p>
              </div>
              {request.destinationAddress?.city && (
                <div>
                  <p className="text-sm text-brown-600 mb-1">עיר</p>
                  <p className="text-lg font-semibold text-brown-900">{request.destinationAddress.city}</p>
                </div>
              )}
              <div>
                <p className="text-sm text-brown-600 mb-1">קומה</p>
                <p className="text-lg font-semibold text-brown-900">{request.destinationAddress?.floor ?? request.destinationFloor ?? 'לא ידוע'}</p>
              </div>
              {(request.destinationAddress?.hasElevator ?? request.destinationHasElevator) !== undefined && (
                <div className="flex items-center">
                  {(request.destinationAddress?.hasElevator ?? request.destinationHasElevator) ? (
                    <div className="flex items-center text-green-600">
                      <CheckCircle className="h-4 w-4 ml-2" />
                      <span className="font-semibold">יש מעלית</span>
                    </div>
                  ) : (
                    <div className="flex items-center text-brown-500">
                      <XCircle className="h-4 w-4 ml-2" />
                      <span className="font-semibold">אין מעלית</span>
                    </div>
                  )}
                </div>
              )}
              {request.destinationAddress?.buildingType && (
                <div>
                  <p className="text-sm text-brown-600 mb-1">סוג הבית</p>
                  <p className="text-lg font-semibold text-brown-900">{request.destinationAddress.buildingType}</p>
                </div>
              )}
              {request.destinationAddress?.numberOfRooms !== undefined && (
                <div>
                  <p className="text-sm text-brown-600 mb-1">מספר חדרים</p>
                  <p className="text-lg font-semibold text-brown-900">{request.destinationAddress.numberOfRooms}</p>
                </div>
              )}
              {request.destinationAddress?.accessPath && request.destinationAddress.accessPath.length > 0 && (
                <div>
                  <p className="text-sm text-brown-600 mb-1">שביל גישה</p>
                  <div className="flex flex-wrap gap-2">
                    {request.destinationAddress.accessPath.map((path, idx) => (
                      <span key={idx} className="inline-block bg-orange-100 text-orange-800 text-sm font-semibold px-3 py-1 rounded-full">
                        {path}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Items */}
        <div className="card mb-8 hover-lift">
          <h3 className="text-xl font-bold text-brown-900 mb-6 flex items-center">
            <div className="bg-orange-100 p-3 rounded-full ml-3">
              <Package className="h-5 w-5 text-orange-600" />
            </div>
            פריטים להובלה
          </h3>
          
          {/* Helper function to render items */}
          {(() => {
            const renderItem = (item, index) => {
              // Backward compatibility: handle string items
              if (typeof item === 'string') {
                return (
                  <div key={index} className="flex items-center justify-between p-3 bg-brown-50 rounded-lg border border-brown-200">
                    <span className="text-brown-900 font-medium">{item}</span>
                  </div>
                );
              }
              
              // New format: object with name, quantity, tags
              const itemName = item.name || '';
              const quantity = item.quantity || 1;
              const tags = item.tags || [];
              
              return (
                <div key={index} className="p-3 bg-brown-50 rounded-lg border border-brown-200 hover:bg-brown-100 transition-colors">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-brown-900 font-medium text-base">{itemName}</span>
                    {quantity > 1 && (
                      <span className="bg-orange-100 text-orange-700 px-2 py-1 rounded-full text-sm font-semibold">
                        כמות: {quantity}
                      </span>
                    )}
                  </div>
                  {tags.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {tags.map((tag, tagIndex) => (
                        <span
                          key={tagIndex}
                          className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-orange-50 text-orange-800 border border-orange-200"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              );
            };
            
            return (
              <div className="space-y-6">
                <div className="grid md:grid-cols-3 gap-4">
                  <div>
                    <p className="text-sm text-brown-600 mb-3 font-semibold">מספר ארגזים</p>
                    <p className="text-brown-900 font-semibold text-lg">
                      {request.numberOfBoxes || 0} ארגזים
                    </p>
                  </div>
                  {request.numberOfBags !== undefined && (
                    <div>
                      <p className="text-sm text-brown-600 mb-3 font-semibold">מספר באלות</p>
                      <p className="text-brown-900 font-semibold text-lg">
                        {request.numberOfBags || 0} באלות
                      </p>
                    </div>
                  )}
                  {request.numberOfClothingBags !== undefined && (
                    <div>
                      <p className="text-sm text-brown-600 mb-3 font-semibold">מספר שקיות בגדים</p>
                      <p className="text-brown-900 font-semibold text-lg">
                        {request.numberOfClothingBags || 0} שקיות
                      </p>
                    </div>
                  )}
                </div>
                
                {request.heavyFurniture?.length > 0 && (
                  <div>
                    <button
                      onClick={() => setIsHeavyFurnitureExpanded(!isHeavyFurnitureExpanded)}
                      className="flex items-center justify-between w-full text-right mb-3 p-2 -mr-2 -ml-2 rounded-lg hover:bg-brown-50 transition-colors"
                      aria-expanded={isHeavyFurnitureExpanded}
                      aria-controls="heavy-furniture-list"
                    >
                      <div className="flex items-center">
                        <span className="text-sm text-brown-600 font-semibold">רהיטים כבדים</span>
                        <span className="mr-2 text-xs text-brown-500 bg-brown-100 px-2 py-1 rounded-full">
                          {request.heavyFurniture.length}
                        </span>
                      </div>
                      {isHeavyFurnitureExpanded ? (
                        <ChevronUp className="h-5 w-5 text-brown-600" />
                      ) : (
                        <ChevronDown className="h-5 w-5 text-brown-600" />
                      )}
                    </button>
                    {isHeavyFurnitureExpanded && (
                      <div id="heavy-furniture-list" className="space-y-2">
                        {request.heavyFurniture.map((item, index) => renderItem(item, index))}
                      </div>
                    )}
                  </div>
                )}
                
                {request.electricalItems?.length > 0 && (
                  <div>
                    <button
                      onClick={() => setIsElectricalItemsExpanded(!isElectricalItemsExpanded)}
                      className="flex items-center justify-between w-full text-right mb-3 p-2 -mr-2 -ml-2 rounded-lg hover:bg-brown-50 transition-colors"
                      aria-expanded={isElectricalItemsExpanded}
                      aria-controls="electrical-items-list"
                    >
                      <div className="flex items-center">
                        <span className="text-sm text-brown-600 font-semibold">מוצרי חשמל</span>
                        <span className="mr-2 text-xs text-brown-500 bg-brown-100 px-2 py-1 rounded-full">
                          {request.electricalItems.length}
                        </span>
                      </div>
                      {isElectricalItemsExpanded ? (
                        <ChevronUp className="h-5 w-5 text-brown-600" />
                      ) : (
                        <ChevronDown className="h-5 w-5 text-brown-600" />
                      )}
                    </button>
                    {isElectricalItemsExpanded && (
                      <div id="electrical-items-list" className="space-y-2">
                        {request.electricalItems.map((item, index) => renderItem(item, index))}
                      </div>
                    )}
                  </div>
                )}
                
                {(!request.heavyFurniture || request.heavyFurniture.length === 0) && 
                 (!request.electricalItems || request.electricalItems.length === 0) && (
                  <div className="text-center py-4">
                    <p className="text-brown-500">אין פריטים להובלה</p>
                  </div>
                )}
              </div>
            );
          })()}
          
          {request.additionalNotes && (
            <div className="mt-6 pt-6 border-t border-brown-200">
              <p className="text-sm text-brown-600 mb-2 font-semibold">הערות נוספות</p>
              <p className="text-brown-900 bg-brown-50 p-3 rounded-lg border border-brown-200">{request.additionalNotes}</p>
            </div>
          )}
        </div>

        {/* Offers */}
        <div className="card mb-8 hover-lift">
          <h3 className="text-xl font-bold text-brown-900 mb-6 flex items-center">
            <div className="bg-orange-100 p-3 rounded-full ml-3">
              <Truck className="h-5 w-5 text-orange-600" />
            </div>
            הצעות מחיר ({request.offers?.length || 0})
          </h3>
          
          {!request.offers || request.offers.length === 0 ? (
            <div className="text-center py-8">
              <Truck className="h-16 w-16 text-brown-300 mx-auto mb-4" />
              <p className="text-brown-600 text-lg">עדיין לא התקבלו הצעות מחיר</p>
              <p className="text-brown-500">המובילים יוכלו לראות את הבקשה שלך ולשלוח הצעות</p>
            </div>
          ) : (
            <div className="space-y-6">
              {request.offers.map((offer, index) => {
                // Create star rating display
                const renderStars = (rating) => {
                  const fullStars = Math.floor(rating);
                  const hasHalfStar = rating % 1 >= 0.5;
                  const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);
                  
                  return (
                    <div className="flex items-center">
                      {[...Array(fullStars)].map((_, i) => (
                        <Star key={i} className="h-4 w-4 text-yellow-400 fill-current" />
                      ))}
                      {hasHalfStar && (
                        <div className="relative">
                          <Star className="h-4 w-4 text-gray-300" />
                          <div className="absolute inset-0 overflow-hidden" style={{ width: '50%' }}>
                            <Star className="h-4 w-4 text-yellow-400 fill-current" />
                          </div>
                        </div>
                      )}
                      {[...Array(emptyStars)].map((_, i) => (
                        <Star key={i + fullStars + (hasHalfStar ? 1 : 0)} className="h-4 w-4 text-gray-300" />
                      ))}
                    </div>
                  );
                };
                
                return (
                <div key={offer._id} className={`border-2 rounded-xl p-6 transition-colors duration-300 ${
                  offer.status === 'withdrawn' 
                    ? 'border-red-200 bg-red-50 hover:border-red-300'
                    : offer.status === 'date_unavailable'
                    ? 'border-orange-200 bg-orange-50 hover:border-orange-300 opacity-75'
                    : 'border-brown-200 hover:border-orange-300'
                }`}>
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <div className="flex items-center mb-2">
                        <h4 className="text-lg font-bold text-brown-900">{offer.driverName}</h4>
                        <div className="mr-3 flex items-center space-x-2 space-x-reverse">
                          <div className="flex items-center">
                            {renderStars(offer.driverRating || 0)}
                            <span className="mr-2 text-sm font-semibold text-brown-700">
                              {offer.driverRating ? offer.driverRating.toFixed(1) : '0.0'}
                            </span>
                          </div>
                          <span className="text-xs text-brown-500 bg-brown-100 px-2 py-1 rounded-full">
                            דירוג: {offer.driverRankingScore ? offer.driverRankingScore.toFixed(1) : '0.0'}/5
                          </span>
                        </div>
                        {offer.status === 'withdrawn' && (
                          <span className={`mr-3 text-xs font-semibold px-2 py-1 rounded-full ${
                            request.offerAcceptedOfferId?.toString() === offer._id.toString()
                              ? 'bg-red-200 text-red-900'
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {request.offerAcceptedOfferId?.toString() === offer._id.toString()
                              ? 'נבחרה ובוטלה'
                              : 'בוטל'
                            }
                          </span>
                        )}
                        {offer.status === 'date_unavailable' && (
                          <span className="mr-3 text-xs font-semibold px-2 py-1 rounded-full bg-orange-200 text-orange-900">
                            {getOfferStatusMessage('date_unavailable')}
                          </span>
                        )}
                      </div>
                      {/* Driver phone visible only if reveal allowed and this is the accepted offer */}
                      {(() => {
                        const isAcceptedOffer = request.offerAcceptedOfferId?.toString() === offer._id.toString();
                        if (request.phoneRevealAllowed && isAcceptedOffer && offer.driverPhone) {
                          return (
                            <p className="text-brown-600 mb-1">טלפון: <a href={`tel:${offer.driverPhone}`} className="underline">{offer.driverPhone}</a></p>
                          );
                        }
                        return null;
                      })()}
                    </div>
                    <div className="text-left">
                      <p className={`text-2xl font-bold ${offer.status === 'date_unavailable' ? 'text-orange-400 line-through' : 'text-orange-600'}`}>
                        ₪{offer.price}
                      </p>
                      <p className="text-sm text-brown-500">מחיר כולל מע"מ</p>
                    </div>
                  </div>
                  
                  {offer.status === 'date_unavailable' && (
                    <div className="mb-4 p-3 bg-orange-100 rounded-lg border border-orange-200">
                      <p className="text-orange-800 font-semibold text-sm">
                        {getOfferStatusMessage('date_unavailable')}
                      </p>
                      <p className="text-orange-700 text-xs mt-1">
                        המוביל כבר קיבל הזמנה אחרת לתאריך זה
                      </p>
                    </div>
                  )}
                  
                  {offer.notes && (
                    <div className="mb-4">
                      <p className="text-sm text-brown-600 mb-1">הערות המוביל:</p>
                      <p className="text-brown-900">{offer.notes}</p>
                    </div>
                  )}
                  
                  <div className="flex justify-between items-center">
                    <div className="flex flex-col">
                      <p className="text-sm text-brown-500">
                        נשלח ב: {new Date(offer.createdAt).toLocaleDateString('he-IL')}
                      </p>
                      {offer.status === 'withdrawn' && offer.withdrawnAt && (
                        <p className="text-sm text-red-500">
                          בוטל ב: {new Date(offer.withdrawnAt).toLocaleDateString('he-IL')}
                        </p>
                      )}
                    </div>
                    <div className="flex space-x-3 space-x-reverse">
                      {offer.status === 'withdrawn' ? (
                        <div className="flex items-center text-red-600">
                          <XCircle className="h-4 w-4 ml-2" />
                          <span className="font-semibold">
                            {request.offerAcceptedOfferId?.toString() === offer._id.toString() 
                              ? 'הצעה שנבחרה בוטלה' 
                              : 'הצעה בוטלה'
                            }
                          </span>
                        </div>
                      ) : request.status === 'offer_accepted' && request.offerAcceptedOfferId?.toString() === offer._id.toString() && offer.status !== 'withdrawn' ? (
                        <div className="flex items-center space-x-3 space-x-reverse">
                          <div className="flex items-center text-green-600">
                            <CheckCircle className="h-4 w-4 ml-2" />
                            <span className="font-semibold">הצעה נבחרה</span>
                          </div>
                          <button
                            onClick={() => {
                              if (offer.driver) {
                                navigate(`/ratings?driverId=${offer.driver}`);
                              } else {
                                logger.error('Cannot navigate to ratings: offer.driver is undefined');
                                toast.error('שגיאה: לא ניתן לטעון ביקורות - מזהה נהג חסר');
                              }
                            }}
                            className="btn-outline px-4 py-2 flex items-center space-x-2 space-x-reverse text-sm"
                          >
                            <Star className="h-4 w-4" />
                            <span>ביקורות</span>
                          </button>
                        </div>
                      ) : request.status === 'offer_accepted' && offer.status !== 'withdrawn' && !request.offers?.some(offer => offer.status === 'withdrawn' && request.offerAcceptedOfferId?.toString() === offer._id.toString()) ? (
                        <div className="flex items-center space-x-3 space-x-reverse">
                          <div className="flex items-center text-gray-500">
                            <XCircle className="h-4 w-4 ml-2" />
                            <span className="font-semibold">הצעה לא נבחרה</span>
                          </div>
                          <button
                            onClick={() => navigate(`/ratings?driverId=${offer.driver}`)}
                            className="btn-outline px-4 py-2 flex items-center space-x-2 space-x-reverse text-sm"
                          >
                            <Star className="h-4 w-4" />
                            <span>ביקורות</span>
                          </button>
                        </div>
                      ) : offer.status === 'date_unavailable' ? (
                        <div className="flex items-center text-orange-600">
                          <XCircle className="h-4 w-4 ml-2" />
                          <span className="font-semibold">{getOfferStatusMessage('date_unavailable')}</span>
                        </div>
                      ) : offer.status === 'pending_reapproval' ? (
                        <div className="flex space-x-3 space-x-reverse">
                          <button
                            onClick={() => acceptOffer(offer._id)}
                            className="btn-primary px-6 py-2"
                          >
                            אשר מחיר חדש
                          </button>
                          <a
                            href={`tel:${offer.driverPhone}`}
                            className="btn-secondary px-6 py-2"
                          >
                            התקשר
                          </a>
                        </div>
                      ) : offer.status !== 'withdrawn' ? (
                        <div className="flex space-x-3 space-x-reverse">
                          <button
                            onClick={() => acceptOffer(offer._id)}
                            className="btn-primary px-6 py-2"
                          >
                            קבל הצעה
                          </button>
                          <a
                            href={`tel:${offer.driverPhone}`}
                            className="btn-secondary px-6 py-2"
                          >
                            התקשר
                          </a>
                          <button
                            onClick={() => navigate(`/ratings?driverId=${offer.driver}`)}
                            className="btn-outline px-6 py-2 flex items-center space-x-2 space-x-reverse"
                          >
                            <Star className="h-4 w-4" />
                            <span>ביקורות</span>
                          </button>
                        </div>
                      ) : null}
                    </div>
                  </div>
                </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="card hover-lift">
          <h3 className="text-xl font-bold text-brown-900 mb-6">פעולות</h3>
          <div className="flex flex-wrap gap-4">
            {!isEditMode && (
              <button
                onClick={handleEditToggle}
                className="btn-secondary px-6 py-3"
              >
                ערוך בקשה
              </button>
            )}
            
            {request.status === 'offer_accepted' && !request.offers?.some(offer => offer.status === 'withdrawn' && request.offerAcceptedOfferId?.toString() === offer._id.toString()) && (
              <button
                onClick={handleCancelOffer}
                className="btn-error px-6 py-3"
              >
                בטל הצעה
              </button>
            )}
            
            {(request.status === 'open' || (request.status === 'offer_accepted' && request.offers?.some(offer => offer.status === 'withdrawn' && request.offerAcceptedOfferId?.toString() === offer._id.toString()))) && (
              <button
                onClick={handleCancelRequest}
                className="btn-error px-6 py-3"
              >
                בטל בקשה
              </button>
            )}
            
            <button
              onClick={fetchRequest}
              className="btn-primary px-6 py-3"
            >
              רענן נתונים
            </button>
          </div>
        </div>

        {/* Edit Form */}
        {isEditMode && (
          <div className="card mb-8 hover-lift">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-bold text-brown-900">עריכת בקשה</h3>
              <div className="flex space-x-3 space-x-reverse">
                <button
                  onClick={handleEditToggle}
                  className="btn-secondary px-4 py-2"
                >
                  ביטול עריכה
                </button>
                <button
                  onClick={updateRequest}
                  disabled={isUpdating}
                  className="btn-primary px-4 py-2"
                >
                  {isUpdating ? 'מעדכן...' : 'שמור שינויים'}
                </button>
              </div>
            </div>
            
            <div className="grid lg:grid-cols-2 gap-6">
              {/* Customer Information */}
              <div>
                <h4 className="text-lg font-semibold text-brown-800 mb-4">פרטי הלקוח</h4>
                <div className="space-y-4">
                  <div>
                    <label htmlFor="edit-customer-name" className="block text-sm font-medium text-brown-700 mb-2">שם הלקוח</label>
                    <input
                      id="edit-customer-name"
                      type="text"
                      value={editData.customerName || ''}
                      onChange={(e) => setEditData({...editData, customerName: e.target.value})}
                      className="input-field w-full"
                      placeholder="שם הלקוח"
                    />
                  </div>
                  <div>
                    <label htmlFor="edit-customer-phone" className="block text-sm font-medium text-brown-700 mb-2">מספר טלפון</label>
                    <input
                      id="edit-customer-phone"
                      type="tel"
                      value={editData.customerPhone || request.customerPhone || ''}
                      className="input-field w-full bg-gray-100"
                      disabled
                      placeholder="מספר טלפון"
                    />
                    <p className="text-xs text-brown-500 mt-1">מספר הטלפון לא ניתן לשינוי</p>
                  </div>
                </div>
              </div>

              {/* Moving Details */}
              <div>
                <h4 className="text-lg font-semibold text-brown-800 mb-4">פרטי ההובלה</h4>
                <div className="space-y-4">
                  <div>
                    <label htmlFor="edit-category" className="block text-sm font-medium text-brown-700 mb-2">סוג הובלה *</label>
                    <select
                      id="edit-category"
                      value={editData.category || MOVING_CATEGORIES.APARTMENT}
                      onChange={(e) => setEditData({...editData, category: e.target.value})}
                      className="input-field w-full"
                      required
                    >
                      {MOVING_CATEGORY_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label htmlFor="edit-preferred-date" className="block text-sm font-medium text-brown-700 mb-2">תאריך הובלה *</label>
                    <input
                      id="edit-preferred-date"
                      type="date"
                      value={editData.preferredDate || ''}
                      onChange={(e) => setEditData({...editData, preferredDate: e.target.value})}
                      className="input-field w-full"
                      required
                    />
                  </div>
                  <div>
                    <label htmlFor="edit-boxes-count" className="block text-sm font-medium text-brown-700 mb-2">מספר ארגזים *</label>
                    <input
                      id="edit-boxes-count"
                      type="number"
                      value={editData.numberOfBoxes || ''}
                      onChange={(e) => setEditData({...editData, numberOfBoxes: parseInt(e.target.value) || 0})}
                      className="input-field w-full"
                      min="0"
                      placeholder="מספר ארגזים"
                      required
                    />
                  </div>
                  <div>
                    <label htmlFor="edit-bags-count" className="block text-sm font-medium text-brown-700 mb-2">מספר באלות *</label>
                    <input
                      id="edit-bags-count"
                      type="number"
                      value={editData.numberOfBags || ''}
                      onChange={(e) => setEditData({...editData, numberOfBags: parseInt(e.target.value) || 0})}
                      className="input-field w-full"
                      min="0"
                      placeholder="מספר באלות"
                      required
                    />
                  </div>
                  <div>
                    <label htmlFor="edit-clothing-bags-count" className="block text-sm font-medium text-brown-700 mb-2">מספר שקיות בגדים *</label>
                    <input
                      id="edit-clothing-bags-count"
                      type="number"
                      value={editData.numberOfClothingBags ?? ''}
                      onChange={(e) => {
                        const val = e.target.value;
                        setEditData({...editData, numberOfClothingBags: val === '' ? '' : parseInt(val, 10)});
                      }}
                      className="input-field w-full"
                      min="0"
                      placeholder="מספר שקיות בגדים"
                      required
                    />
                  </div>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="requiresCrane"
                      checked={editData.requiresCrane || false}
                      onChange={(e) => setEditData({...editData, requiresCrane: e.target.checked})}
                      className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
                    />
                    <label htmlFor="requiresCrane" className="mr-2 text-sm font-medium text-brown-700">
                      נדרש מנוף
                    </label>
                  </div>
                </div>
              </div>
            </div>

            {/* Addresses */}
            <div className="grid lg:grid-cols-2 gap-6 mt-6">
              {/* Origin Address */}
              <div>
                <h4 className="text-lg font-semibold text-brown-800 mb-4">כתובת המוצא</h4>
                <div className="space-y-4">
                  <div>
                    <AddressAutocomplete
                      id="edit-origin-address"
                      label="כתובת המוצא"
                      value={editData.originAddress || ''}
                      onChange={(e) => setEditData({...editData, originAddress: e.target.value})}
                      onValidationChange={(validationData) => {
                        if (validationData.isValid) {
                          setOriginAddressData(validationData);
                          setEditData({...editData, originAddress: validationData.address});
                        } else {
                          setOriginAddressData(null);
                        }
                      }}
                      required={true}
                      helpText="התחל להקליד כתובת ותקבל הצעות אוטומטיות"
                      placeholder="הזן את כתובת המוצא"
                    />
                  </div>
                  <div>
                    <label htmlFor="edit-origin-floor" className="block text-sm font-medium text-brown-700 mb-2">קומה</label>
                    <input
                      id="edit-origin-floor"
                      type="text"
                      value={editData.originFloor || ''}
                      onChange={(e) => setEditData({...editData, originFloor: e.target.value})}
                      className="input-field w-full"
                      placeholder="קומה"
                    />
                  </div>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="originHasElevator"
                      checked={editData.originHasElevator || false}
                      onChange={(e) => setEditData({...editData, originHasElevator: e.target.checked})}
                      className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
                    />
                    <label htmlFor="originHasElevator" className="mr-2 text-sm font-medium text-brown-700">
                      יש מעלית
                    </label>
                  </div>
                  {editData.category === MOVING_CATEGORIES.APARTMENT && (
                    <>
                      <div>
                        <label htmlFor="edit-origin-building-type" className="block text-sm font-medium text-brown-700 mb-2">סוג הבית</label>
                        <select
                          id="edit-origin-building-type"
                          value={editData.originBuildingType || ''}
                          onChange={(e) => setEditData({...editData, originBuildingType: e.target.value})}
                          className="input-field w-full"
                        >
                          <option value="">-- בחר סוג בית --</option>
                          {BUILDING_TYPE_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label htmlFor="edit-origin-number-of-rooms" className="block text-sm font-medium text-brown-700 mb-2">מספר חדרים</label>
                        <input
                          id="edit-origin-number-of-rooms"
                          type="number"
                          value={editData.originNumberOfRooms ?? ''}
                          onChange={(e) => setEditData({...editData, originNumberOfRooms: e.target.value === '' ? '' : parseInt(e.target.value, 10)})}
                          className="input-field w-full"
                          min="0"
                          max="20"
                          placeholder="מספר חדרים"
                        />
                      </div>
                      <div>
                        <span className="block text-sm font-medium text-brown-700 mb-2">שביל גישה</span>
                        <div className="space-y-2">
                          {ACCESS_PATH_OPTIONS_ARRAY.map((option) => (
                            <div key={option.value} className="flex items-center">
                              <input
                                type="checkbox"
                                id={`origin-access-${option.value}`}
                                checked={(editData.originAccessPath || []).includes(option.value)}
                                onChange={(e) => {
                                  const currentPath = editData.originAccessPath || [];
                                  if (e.target.checked) {
                                    setEditData({...editData, originAccessPath: [...currentPath, option.value]});
                                  } else {
                                    setEditData({...editData, originAccessPath: currentPath.filter(v => v !== option.value)});
                                  }
                                }}
                                className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
                              />
                              <label htmlFor={`origin-access-${option.value}`} className="mr-2 text-sm font-medium text-brown-700">
                                {option.label}
                              </label>
                            </div>
                          ))}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Destination Address */}
              <div>
                <h4 className="text-lg font-semibold text-brown-800 mb-4">כתובת היעד</h4>
                <div className="space-y-4">
                  <div>
                    <AddressAutocomplete
                      id="edit-destination-address"
                      label="כתובת היעד"
                      value={editData.destinationAddress || ''}
                      onChange={(e) => setEditData({...editData, destinationAddress: e.target.value})}
                      onValidationChange={(validationData) => {
                        if (validationData.isValid) {
                          setDestinationAddressData(validationData);
                          setEditData({...editData, destinationAddress: validationData.address});
                        } else {
                          setDestinationAddressData(null);
                        }
                      }}
                      required={true}
                      helpText="התחל להקליד כתובת ותקבל הצעות אוטומטיות"
                      placeholder="הזן את כתובת היעד"
                    />
                  </div>
                  <div>
                    <label htmlFor="edit-destination-floor" className="block text-sm font-medium text-brown-700 mb-2">קומה</label>
                    <input
                      id="edit-destination-floor"
                      type="text"
                      value={editData.destinationFloor || ''}
                      onChange={(e) => setEditData({...editData, destinationFloor: e.target.value})}
                      className="input-field w-full"
                      placeholder="קומה"
                    />
                  </div>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="destinationHasElevator"
                      checked={editData.destinationHasElevator || false}
                      onChange={(e) => setEditData({...editData, destinationHasElevator: e.target.checked})}
                      className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
                    />
                    <label htmlFor="destinationHasElevator" className="mr-2 text-sm font-medium text-brown-700">
                      יש מעלית
                    </label>
                  </div>
                  {editData.category === MOVING_CATEGORIES.APARTMENT && (
                    <>
                      <div>
                        <label htmlFor="edit-destination-building-type" className="block text-sm font-medium text-brown-700 mb-2">סוג הבית</label>
                        <select
                          id="edit-destination-building-type"
                          value={editData.destinationBuildingType || ''}
                          onChange={(e) => setEditData({...editData, destinationBuildingType: e.target.value})}
                          className="input-field w-full"
                        >
                          <option value="">-- בחר סוג בית --</option>
                          {BUILDING_TYPE_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label htmlFor="edit-destination-number-of-rooms" className="block text-sm font-medium text-brown-700 mb-2">מספר חדרים</label>
                        <input
                          id="edit-destination-number-of-rooms"
                          type="number"
                          value={editData.destinationNumberOfRooms ?? ''}
                          onChange={(e) => setEditData({...editData, destinationNumberOfRooms: e.target.value === '' ? '' : parseInt(e.target.value, 10)})}
                          className="input-field w-full"
                          min="0"
                          max="20"
                          placeholder="מספר חדרים"
                        />
                      </div>
                      <div>
                        <span className="block text-sm font-medium text-brown-700 mb-2">שביל גישה</span>
                        <div className="space-y-2">
                          {ACCESS_PATH_OPTIONS_ARRAY.map((option) => (
                            <div key={option.value} className="flex items-center">
                              <input
                                type="checkbox"
                                id={`destination-access-${option.value}`}
                                checked={(editData.destinationAccessPath || []).includes(option.value)}
                                onChange={(e) => {
                                  const currentPath = editData.destinationAccessPath || [];
                                  if (e.target.checked) {
                                    setEditData({...editData, destinationAccessPath: [...currentPath, option.value]});
                                  } else {
                                    setEditData({...editData, destinationAccessPath: currentPath.filter(v => v !== option.value)});
                                  }
                                }}
                                className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
                              />
                              <label htmlFor={`destination-access-${option.value}`} className="mr-2 text-sm font-medium text-brown-700">
                                {option.label}
                              </label>
                            </div>
                          ))}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Items */}
            <div className="mt-6">
              <h4 className="text-lg font-semibold text-brown-800 mb-4">פריטים להובלה</h4>
              <div className="grid lg:grid-cols-2 gap-6">
                <ItemSelector
                  id="edit-heavy-furniture"
                  items={HEAVY_FURNITURE_ITEMS}
                  selectedItems={editData.heavyFurniture || []}
                  onAdd={(item) => {
                    const newItem = { ...item };
                    if (!newItem.id && !newItem.isCustom) {
                      const catalogItem = HEAVY_FURNITURE_ITEMS.find(i => i.name === item.name);
                      if (catalogItem) newItem.id = catalogItem.id;
                    }
                    if (!newItem.id) newItem.id = `hf-${Date.now()}-${Math.random()}`;
                    
                    setEditData({
                      ...editData,
                      heavyFurniture: [...(editData.heavyFurniture || []), newItem]
                    });
                  }}
                  onRemove={(index) => {
                    const updated = [...(editData.heavyFurniture || [])];
                    updated.splice(index, 1);
                    setEditData({ ...editData, heavyFurniture: updated });
                  }}
                  onUpdate={(index, item) => {
                    const updated = [...(editData.heavyFurniture || [])];
                    updated[index] = { ...item, id: updated[index].id || `hf-${Date.now()}-${Math.random()}` };
                    setEditData({ ...editData, heavyFurniture: updated });
                  }}
                  categoryLabel="רהיטים כבדים"
                  helpText="בחר רהיטים מהרשימה או הוסף פריטים מותאמים"
                />

                <ItemSelector
                  id="edit-electrical-items"
                  items={ELECTRICAL_ITEMS}
                  selectedItems={editData.electricalItems || []}
                  onAdd={(item) => {
                    const newItem = { ...item };
                    if (!newItem.id && !newItem.isCustom) {
                      const catalogItem = ELECTRICAL_ITEMS.find(i => i.name === item.name);
                      if (catalogItem) newItem.id = catalogItem.id;
                    }
                    if (!newItem.id) newItem.id = `ei-${Date.now()}-${Math.random()}`;
                    
                    setEditData({
                      ...editData,
                      electricalItems: [...(editData.electricalItems || []), newItem]
                    });
                  }}
                  onRemove={(index) => {
                    const updated = [...(editData.electricalItems || [])];
                    updated.splice(index, 1);
                    setEditData({ ...editData, electricalItems: updated });
                  }}
                  onUpdate={(index, item) => {
                    const updated = [...(editData.electricalItems || [])];
                    updated[index] = { ...item, id: updated[index].id || `ei-${Date.now()}-${Math.random()}` };
                    setEditData({ ...editData, electricalItems: updated });
                  }}
                  categoryLabel="מוצרי חשמל"
                  helpText="בחר מוצרי חשמל מהרשימה או הוסף פריטים מותאמים"
                />
              </div>
              <div className="mt-4">
                <label htmlFor="edit-additional-notes" className="block text-sm font-medium text-brown-700 mb-2">הערות נוספות</label>
                <textarea
                  id="edit-additional-notes"
                  value={editData.additionalNotes || ''}
                  onChange={(e) => setEditData({...editData, additionalNotes: e.target.value})}
                  className="input-field w-full"
                  rows={3}
                  placeholder="הערות נוספות על ההובלה"
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Phone Verification Modal */}
      {showVerification && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div 
            className="bg-white rounded-2xl p-8 max-w-md w-full mx-4"
            role="dialog"
            aria-modal="true"
          >
            <h3 className="text-xl font-bold text-brown-900 mb-6 text-center">אימות מספר טלפון</h3>
            <p className="text-brown-700 mb-6 text-center">
              נשלח קוד אימות למספר הטלפון: {request?.maskedCustomerPhone || '***'}
            </p>
            <div className="space-y-4">
              <div className="flex space-x-3 space-x-reverse">
                <button
                  onClick={async () => {
                    try {
                      await axios.post(`${API_ENDPOINTS.SMS}/send-verification`, {
                        phone: request.customerPhone
                      });
                      toast.success('נשלח קוד אימות לטלפון שלך');
                    } catch (error) {
                      toast.error(error.response?.data?.message || 'שגיאה בשליחת קוד האימות');
                    }
                  }}
                  className="btn-secondary flex-1"
                >
                  שלח קוד אימות
                </button>
              </div>
              <input
                type="text"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value)}
                placeholder="הזן קוד אימות"
                className="input-field text-center text-2xl tracking-widest"
                maxLength={6}
              />
              <div className="flex space-x-3 space-x-reverse">
                <button
                  onClick={() => setShowVerification(false)}
                  className="btn-secondary flex-1"
                >
                  ביטול
                </button>
                <button
                  onClick={verifyCode}
                  disabled={isVerifying}
                  className="btn-primary flex-1"
                >
                  {isVerifying ? 'מאמת...' : 'אמת'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Confirmation Modal */}
      {showEditConfirmationModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full mx-4">
            <h3 className="text-xl font-bold text-brown-900 mb-6 text-center">אישור עריכת בקשה</h3>
            <div className="mb-6 p-4 bg-orange-50 rounded-xl">
              <p className="text-brown-700 mb-4 text-center font-semibold">
                שים לב! הבקשה שלך כבר התקבלה על ידי מוביל.
              </p>
              <p className="text-brown-600 text-center text-sm">
                ביצוע שינויים בפרטי ההובלה יאפשר למוביל לעדכן את הצעת המחיר שלו בהתאם לשינויים.
                במקרה שהמוביל יערוך את המחיר, יהיה עליך לאשר את המחיר החדש.
              </p>
            </div>
            <p className="text-brown-700 mb-6 text-center">
              האם להמשיך ולשמור את השינויים?
            </p>
            <div className="flex space-x-3 space-x-reverse">
              <button
                onClick={() => setShowEditConfirmationModal(false)}
                className="btn-secondary flex-1"
              >
                ביטול
              </button>
              <button
                onClick={() => {
                  setShowEditConfirmationModal(false);
                  updateRequest(true);
                }}
                className="btn-primary flex-1"
              >
                אני מסכים - עדכן בקשה
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cancel Offer Modal */}
      {showCancelOfferModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full mx-4">
            <h3 className="text-xl font-bold text-brown-900 mb-6 text-center">ביטול הצעה</h3>
            {(() => {
              const cancellationInfo = getCancellationInfo();
              if (!cancellationInfo) return null;
              
              return (
                <div className="mb-6 p-4 bg-orange-50 rounded-xl">
                  <p className="text-brown-700 mb-2 text-center">
                    {cancellationInfo.canCancelFree ? (
                      <span className="text-green-600 font-semibold">ניתן לבטל ללא עלות</span>
                    ) : (
                      <span className="text-orange-600 font-semibold">
                        עלות ביטול: {cancellationInfo.cancellationFeePercentage}% (₪{cancellationInfo.cancellationFee})
                      </span>
                    )}
                  </p>
                  <p className="text-sm text-brown-600 text-center">
                    {cancellationInfo.hoursSinceAcceptance} שעות מאז קבלת ההצעה
                  </p>
                </div>
              );
            })()}
            <p className="text-brown-700 mb-6 text-center">
              האם אתה בטוח שברצונך לבטל את ההצעה?
            </p>
            <div className="space-y-4">
              <textarea
                value={cancelOfferReason}
                onChange={(e) => setCancelOfferReason(e.target.value)}
                placeholder="סיבת הביטול (אופציונלי)"
                maxLength={500}
                className="input-field"
                rows={3}
              />
              <div className="flex space-x-3 space-x-reverse">
                <button
                  onClick={() => setShowCancelOfferModal(false)}
                  className="btn-secondary flex-1"
                >
                  ביטול
                </button>
                <button
                  onClick={cancelOffer}
                  className="btn-error flex-1"
                >
                  בטל הצעה
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Cancel Modal */}
      {showCancelModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full mx-4">
            <h3 className="text-xl font-bold text-brown-900 mb-6 text-center">ביטול בקשה</h3>
            <p className="text-brown-700 mb-6 text-center">
              האם אתה בטוח שברצונך לבטל את הבקשה? פעולה זו אינה הפיכה.
            </p>
            <div className="space-y-4">
              <textarea
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                placeholder="סיבת הביטול (אופציונלי)"
                maxLength={500}
                className="input-field"
                rows={3}
              />
              <div className="flex space-x-3 space-x-reverse">
                <button
                  onClick={() => setShowCancelModal(false)}
                  className="btn-secondary flex-1"
                >
                  ביטול
                </button>
                <button
                  onClick={cancelRequest}
                  className="btn-error flex-1"
                >
                  בטל בקשה
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Driver Rating Modal */}
      {showRatingModal && request.selectedDriver && (
        <DriverRatingModal
          isOpen={showRatingModal}
          onClose={() => setShowRatingModal(false)}
          requestId={request.id}
          driverId={request.selectedDriver}
          driverName={request.selectedDriverName}
          customerPhone={request.customerPhone}
        />
      )}

      {/* Job Completion Modal */}
      {showCloseModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full">
            <h3 className="text-xl font-bold text-brown-900 mb-6 text-center">אשר השלמת הובלה</h3>
            
            <div className="mb-6 p-4 bg-green-50 rounded-lg">
              <p className="text-green-700 text-center">
                <strong>מוביל:</strong> {request.selectedDriverName}
              </p>
              <p className="text-green-700 text-center mt-1">
                <strong>מחיר מוצע:</strong> ₪{request.selectedPrice}
              </p>
            </div>

            <div className="mb-6">
              <label htmlFor="final-price" className="form-label">מחיר סופי (אופציונלי)</label>
              <input
                id="final-price"
                type="number"
                value={finalPrice}
                onChange={(e) => setFinalPrice(e.target.value)}
                className="input-field"
                placeholder={`₪${request.selectedPrice}`}
                min="0"
                step="0.01"
              />
              <p className="text-sm text-brown-500 mt-1">
                השאר ריק אם המחיר לא השתנה
              </p>
            </div>

            <p className="text-brown-700 mb-6 text-center">
              האם אתה בטוח שההובלה הושלמה בהצלחה?
            </p>
            
            <div className="flex space-x-3 space-x-reverse">
              <button
                type="button"
                onClick={() => {
                  setShowCloseModal(false);
                  setFinalPrice('');
                }}
                className="btn-secondary flex-1"
                disabled={isClosing}
              >
                ביטול
              </button>
              <button
                onClick={handleJobCompletion}
                className="btn-primary flex-1"
                disabled={isClosing}
              >
                {isClosing ? 'סוגר...' : 'אשר השלמה'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RequestTrackingPage;
